import React from 'react';
import { Box, Typography, Paper, Grid, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { VideoStatus } from './types';

interface NotificationProps {
  videos: VideoStatus[];
}

// Wrapper that slides vertically from/to the bottom
const NotificationWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ visible }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  transform: visible ? 'translateY(0)' : 'translateY(150%)',
  transition: 'transform 0.3s ease-in-out',
  pointerEvents: visible ? 'auto' : 'none',
}));

const ExpandedWrapperBox = styled(Box)({
  marginTop: '16px',
});

const HeadingContainer = styled(Box)({
  position: 'relative',
});

const ExpandIconContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  color: '#fff',
  opacity: 0.85,
  pointerEvents: 'none', // icon is decorative; whole item is clickable
});

const NotificationItem = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: '16px',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  borderRadius: '4px',
  minWidth: '260px',
  maxWidth: '420px',
  // Remove horizontal slide-in animation to avoid conflict with vertical slide
  cursor: 'pointer',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  },
}));

export const VideoProgressNotification: React.FC<NotificationProps> = ({
  videos,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setExpanded((prev) => !prev);
    }
  };

  const importingVideoSize = videos.filter(video => !['completed', 'failed'].includes(video.status)).length;
  const visible = videos.length > 0;

  return (
    <NotificationWrapper visible={visible} aria-hidden={!visible}>
      <NotificationItem
        elevation={3}
        role={'button'}
        aria-expanded={expanded}
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        title={expanded ? 'Click to collapse' : 'Click to expand'}
      >
        <HeadingContainer>
          <Typography
            variant="body2"
            noWrap={!expanded} // single-line by default, full text when expanded
            sx={{
              whiteSpace: expanded ? 'pre-wrap' : undefined, // keep formatting when expanded
            }}
          >
            {expanded ? `Background task:` : `${importingVideoSize} video${importingVideoSize === 1 ? '' : 's'} importing...`}
          </Typography>
          <ExpandIconContainer aria-hidden="true">
            {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
          </ExpandIconContainer>
        </HeadingContainer>

        {expanded && (
          <ExpandedWrapperBox>
            <Grid container>
              {videos.map((video) => (
                <Grid key={video.id} item xs={12} marginBottom={1}>
                  <Typography variant="body2">Status: {video.video_message}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={video.progress}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                  <Typography variant="body2">Video filename: {video.video_filename}</Typography>
                </Grid>
              ))}
            </Grid>
          </ExpandedWrapperBox>
        )}
      </NotificationItem>
    </NotificationWrapper>
  );
};